import React from "react"

import { Check } from "lucide-react"

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import type { EmptyLegsDetail, VehicleCapacityInfo } from "@/lib/routeCostCalculation"
import {
  COVER_LABEL,
  coverTextClass,
  coverTitle,
  describeCover,
  displayPerson,
  formatWindow,
  worstException,
} from "@/lib/shiftCover"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type { CoveredRoute, ShiftException } from "@/types/collections"

import CellSpinner from "./CellSpinner"
import type { NotCheckedReason } from "./usePlanningShiftCover"

export const LoadFactorCell: React.FC<{
  loading: boolean
  /** Null without a known vehicle type or a stop that takes up capacity. */
  capacityInfo: VehicleCapacityInfo | null
}> = ({ loading, capacityInfo }) => {
  if (loading) return <CellSpinner />
  if (!capacityInfo) return "-"

  const contributions = Object.entries(capacityInfo.usedCapacity || {})
    .filter(([_, used]) => used > 0)
    .map(([type, used]) => {
      const typeKey = type as keyof typeof capacityInfo.vehicleCapacity
      const capacity = capacityInfo.vehicleCapacity?.[typeKey] || 0
      const fill = capacity > 0 ? (used / capacity) * 100 : 0
      if (isNaN(fill) || !isFinite(fill)) return null
      const typeName = type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      return `${used} ${typeName} (${fill.toFixed(0)}%)`
    })
    .filter((item): item is string => item !== null)

  const fillPct = capacityInfo.totalFillPercentage
  if (isNaN(fillPct) || !isFinite(fillPct)) return "-"

  const loadFactorColor =
    fillPct >= 100
      ? "text-red-600 font-semibold"
      : fillPct >= 80
        ? "text-amber-600 font-medium"
        : "text-palette-jungleGreen900 font-medium"

  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className={cn("text-xs font-medium", loadFactorColor)}>{fillPct.toFixed(0)}%</span>
      <span className="text-[10px] text-palette-jungleGreen500">
        {contributions.join(", ") || "Empty"}
      </span>
    </div>
  )
}

export const ShiftCoverCell: React.FC<{
  loading: boolean
  cover: ShiftException[] | undefined
  covered: CoveredRoute[] | undefined
  notCheckedReason: NotCheckedReason | null
}> = ({ loading, cover, covered, notCheckedReason }) => {
  if (loading) return <CellSpinner />
  const differs = cn(toneClasses("warning").text, "font-medium")
  const sub = "truncate text-[10px] text-palette-jungleGreen500"

  if (cover && cover.length > 0) {
    const worst = worstException(cover)
    const { timeDiffers, driverDiffers, words } = describeCover(cover)
    const shifts = [
      ...new Map(cover.filter((r) => r.shiftId).map((r) => [r.shiftId, r])).values(),
    ]
    return (
      <div className="flex flex-col gap-0.5" title={coverTitle(cover)}>
        <span className={cn("text-xs", coverTextClass(worst.severity))}>
          {COVER_LABEL[worst.exceptionType]}
        </span>
        {shifts.map((sh) => {
          const unfilled = sh.shiftStatus && sh.shiftStatus !== "Filled"
          return (
            <span key={sh.shiftId} className={sub}>
              <span
                className={cn(driverDiffers && differs, unfilled && coverTextClass("unstaffed"))}
              >
                {displayPerson(sh.shiftPersonName) ?? "Unassigned"}
                {unfilled ? ` ${sh.shiftStatus}` : ""}
              </span>
              {" · "}
              <span className={cn("tabular-nums", timeDiffers && differs)}>
                {formatWindow(sh.shiftStart, sh.shiftEnd)}
              </span>
            </span>
          )
        })}
        {words.length > 0 && <span className={sub}>{words.join(" · ")}</span>}
      </div>
    )
  }

  // A double-manned route has one row per seat.
  if (covered && covered.length > 0) {
    return (
      <div
        className="flex flex-col gap-0.5"
        title={covered.map((c) => `Shift ${formatWindow(c.shiftStart, c.shiftEnd)}`).join("\n")}
      >
        <span
          className={cn(
            "flex items-center gap-1 text-xs font-medium",
            toneClasses("success").text,
          )}
        >
          <Check size={12} className="flex-shrink-0" />
          Covered
        </span>
        {covered.map((c) => (
          <span key={c.shiftId} className={sub}>
            {displayPerson(c.shiftPersonName) ?? "Unassigned"}
            {" · "}
            <span className="tabular-nums">{formatWindow(c.shiftStart, c.shiftEnd)}</span>
          </span>
        ))}
      </div>
    )
  }

  return (
    <span className="text-[10px] italic text-palette-jungleGreen400" title={notCheckedReason?.why}>
      {notCheckedReason?.label ?? "—"}
    </span>
  )
}

export const EmptyLegsCell: React.FC<{ emptyLegs: EmptyLegsDetail }> = ({ emptyLegs }) => {
  if (emptyLegs.count === 0) return <span className="text-palette-jungleGreen400">0%</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex flex-col items-end gap-0.5 cursor-help">
          <span className="text-xs font-medium text-red-600">
            {emptyLegs.emptyPct != null ? `${emptyLegs.emptyPct}%` : "—"}
          </span>
          <span className="text-[10px] text-palette-jungleGreen500">
            {emptyLegs.count} leg
            {emptyLegs.count !== 1 ? "s" : ""} · {emptyLegs.miles.toFixed(1)} mi
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        <p className="text-xs">
          Empty legs: {emptyLegs.miles.toFixed(1)} mi of{" "}
          {emptyLegs.totalMiles != null ? emptyLegs.totalMiles.toFixed(1) : "—"} mi total (
          {emptyLegs.emptyPct != null ? `${emptyLegs.emptyPct}%` : "—"})
        </p>
      </TooltipContent>
    </Tooltip>
  )
}
